import React from 'preact/compat';
export {
  useCallback,
  useEffect,
  useState,
  lazy,
  Suspense,
  useMemo,
  useRef,
  forwardRef,
  createContext,
  useId,
  useContext,
  useLayoutEffect,
  createElement,
  createPortal,
  flushSync,
  Fragment,
  PureComponent,
  Component,
  isValidElement,
  Children,
  useImperativeHandle,
  cloneElement,
  startTransition,
  useSyncExternalStore,
  useReducer,
  memo,
  useDebugValue,
} from 'preact/compat';

import { h, createContext, Component, ComponentChildren } from 'preact';
import { createRoot } from 'preact/compat/client';

const ActivityContext = createContext<'visible' | 'hidden'>('visible');

class ActivityFreezer extends Component<{ hidden: boolean; children: any }> {
  override shouldComponentUpdate(nextProps: { hidden: boolean }) {
    // When hidden, block ALL re-renders → no effects fire
    // When transitioning back to visible, allow update
    return !nextProps.hidden;
  }

  render() {
    return this.props.children;
  }
}

function Activity({
  mode,
  children,
}: {
  mode: 'visible' | 'hidden';
  children: ComponentChildren[];
}) {
  const hidden = mode === 'hidden';

  return h(
    ActivityContext.Provider,
    { value: mode },
    h(
      'div',
      {
        style: { display: hidden ? 'none' : 'contents' },
        'aria-hidden': hidden || undefined,
      },
      h(ActivityFreezer, { hidden, children }, children)
    )
  );
}
const useEffectEvent = <T extends (...args: any[]) => any>(fn: T): T => {
  const ref = React.useRef<T>(fn);
  ref.current = fn;

  return React.useCallback(
    ((...args: any[]) => (ref.current ? ref.current(...args) : () => {})) as T,
    []
  );
};

interface Thenable<T> {
  then(onFulfill: (value: T) => void, onReject?: (err: any) => void): void;
}

const enum Status {
  Pending,
  Fulfilled,
  Rejected,
}

type PendingResult = { status: Status.Pending; value: Promise<unknown> };
type FulfilledResult<T> = { status: Status.Fulfilled; value: T };
type RejectedResult = { status: Status.Rejected; value: unknown };
type Result<T> = PendingResult | FulfilledResult<T> | RejectedResult;

const cache = new WeakMap<Thenable<any>, Result<any>>();

const trackPromise = <T>(thenable: Thenable<T>): Result<T> => {
  const existing = cache.get(thenable);
  if (existing) {
    return existing;
  }

  const result: PendingResult = {
    status: Status.Pending,
    value:
      typeof (thenable as any).then === 'function'
        ? new Promise<T>((resolve, reject) => {
            thenable.then(
              (v) => {
                (result as unknown as FulfilledResult<T>).status = Status.Fulfilled;
                (result as unknown as FulfilledResult<T>).value = v;
                resolve(v);
              },
              (e) => {
                (result as unknown as RejectedResult).status = Status.Rejected;
                (result as unknown as RejectedResult).value = e;
                reject(e);
              }
            );
          })
        : (thenable as unknown as Promise<T>),
  };

  cache.set(thenable, result);
  return result;
};

const isThenable = <T>(value: any): value is Thenable<T> =>
  value != null &&
  (typeof value === 'object' || typeof value === 'function') &&
  typeof value.then === 'function';

const use = <T>(usable: preact.Context<T> | Thenable<T>): T => {
  // Thenables have .then — contexts don't
  if (isThenable<T>(usable)) {
    const result = trackPromise(usable);
    switch (result.status) {
      case Status.Fulfilled:
        return result.value;
      case Status.Rejected:
        throw result.value;
      case Status.Pending:
        throw result.value;
    }
  }

  // Everything else is assumed to be a context
  return React.useContext(usable as preact.Context<T>);
};

export { use, createRoot, useEffectEvent, Activity, ActivityContext };

export default React;
